import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router';
import { CallHistoryMethodAction, push } from 'connected-react-router';
import {
  PaymentIntent,
  PaymentMethod,
  PaymentRequestOptions,
} from '@stripe/stripe-js';

import styled from 'styled-components';

import { Translate } from 'react-localize-redux';

// Store
import AppState from '@webapp/store/state';
import { releaseCheckout } from '@webapp/store/release/selectors';
import {
  PublishRelease,
  UpdateField,
} from '@webapp/store/release/action-creators';

// Types
import { Order } from '@webapp/types/orders/order';

// Types
import { Release } from '@common/types/release/release';
import SimpleArtist from '@common/types/artist/simple-artist';

// Controllers
import StripePaymentCtrl from '@webapp/controllers/payment/stripe-payment-element';
import PaymentRequestButton from '@webapp/controllers/payment/payment-request-btn';
import { PayPalPaymentButton } from '@webapp/controllers/payment/paypal/payment-button';
import { ButtonLoadingCtrl } from '@webapp/controllers/button-loading';

// Components
import Flex from '@components/layout/flex';
import BackIcon from '@components/basics/icons/back';
import Typography from '@components/basics/typography';
import Box from '@components/layout/box';
import BoxHeader from '@components/layout/box-header';
import OrderCtrl from '@webapp/controllers/order';
import { Coupon } from '@services/product/types/coupon';

interface PreviewPageProps {
  className?: string;
  artist: SimpleArtist;
  title: string;
  basket: Order[];
  release: Partial<Release>;
  updatePaidItems: (basket: Order[]) => UpdateField<'paidItems'>;
  publish: (
    artistId: string,
    releaseId: string,
    release: Partial<Release>
  ) => PublishRelease;
  goTo: (url: string) => CallHistoryMethodAction;
  isMobile: boolean;
  activeCoupon?: Coupon;
}

const mapStateToProps = (state: AppState) => ({
  artist: state.artist.active,
  title: state.release.release?.title as string,
  basket: releaseCheckout(state),
  release: state.release.release,
  isMobile: state.ui.mobile,
  activeCoupon: state.products.coupons.find((c) => c.isActive),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  publish: (artistId: string, releaseId: string, release: Partial<Release>) =>
    dispatch(new PublishRelease(artistId, releaseId, release)),
  updatePaidItems: (basket: Order[]) =>
    dispatch(new UpdateField<'paidItems'>('paidItems', basket, true)),
  goTo: (url: string) => dispatch(push(url)),
});

const getPaymentRequest = (value: number): PaymentRequestOptions => {
  return {
    country: 'US',
    currency: 'usd',
    total: {
      label: 'Payment',
      amount: value,
    },
    requestPayerName: true,
    requestPayerEmail: true,
  };
};

export const PreviewPage = (props: PreviewPageProps) => {
  const { artist, basket, title, release, updatePaidItems, publish } = props;
  const history = useHistory();
  const match = useRouteMatch<{
    type: string;
    artistId: string;
    releaseId: string;
  }>();

  const { artistId, releaseId } = match.params;
  const total = basket.reduce((t, o) => t + o.price.value * o.quantity, 0);
  const [loading, setLoading] = React.useState(false);
  const paymentBtn =
    React.useRef() as React.MutableRefObject<HTMLButtonElement>;

  const [paymentRequestOptions, setPaymentRequestOptions] =
    React.useState<PaymentRequestOptions>();

  React.useEffect(() => {
    setPaymentRequestOptions(getPaymentRequest(total));
  }, [total]);

  const continueToPublish = React.useCallback(
    (source: 'stripe' | 'paypal', data?: PaymentIntent) => {
      const newBasket = (release.paidItems || []).concat(
        basket.map((item) => ({
          ...item,
          source,
          date: Number(new Date()),
          transactionId: data?.id,
        }))
      );

      updatePaidItems(newBasket);

      publish(artistId, releaseId as string, {
        ...release,
        paidItems: newBasket,
      });
    },
    [artistId, releaseId, basket, release, updatePaidItems, publish]
  );

  const handlePaymentMethod = async (
    _paymentMethod: PaymentMethod,
    complete: Function
  ): Promise<void> => {
    if (!(artist?.id && release && release.id)) {
      return;
    }
    complete('success');
  };

  const goBack = () => {
    if (props.release?.id) {
      history.goBack();
    }
  };

  return artist?.id ? (
    <div className={props.className}>
      <BackIcon className="back-icon" onClick={goBack} />

      <Typography
        className="mb-30"
        type="subheader"
        customFont="semi-bold"
        weight="400"
      >
        <Translate id="PAYMENT.TITLE" />
      </Typography>

      <BoxWrapper>
        <Box noBorderBottom>
          <BoxHeader
            title={<Translate id="PAYMENT.QUICK_PAYMENT.TITLE" />}
            text={<Translate id="PAYMENT.QUICK_PAYMENT.TEXT" />}
          />
          <PaymentBtnWrapper>
            {total > 0 ? (
              <PayPalPaymentButton
                className="full-width"
                amount={total / 100}
                onSuccess={() => continueToPublish('paypal')}
              />
            ) : null}
            <PaymentRequestButton
              paymentRequestOptions={paymentRequestOptions}
              onPaymentMethod={handlePaymentMethod}
            />
          </PaymentBtnWrapper>
        </Box>
        {total > 0 && (
          <StripePaymentCtrl
            paymentOptions={{
              mode: 'payment',
              amount: total,
              setupFutureUsage: 'on_session',
              currency: 'usd',
            }}
            paymentIntent={{
              ownerId: artist.id,
              amount: total,
              description: title || '',
              customer: {
                id: artist.id,
                name: artist?.name || '',
              },
              setUpFutureUsage: 'on_session',
            }}
            redirectUrl={`${window.location.origin}/release/${artistId}/${releaseId}/payment-success`}
            onError={() => setLoading(false)}
            isMobile={props.isMobile}
            paymentBtnRef={paymentBtn}
            onSuccess={(_method, _stripe, _intent) => {
              setLoading(false);
            }}
          />
        )}
        <OrderCtrl className="order" basket={props.basket}>
          <ButtonLoadingCtrl
            className="btn-loading"
            isLoading={loading}
            disabled={false}
            fullWidth
            onClick={() => {
              setLoading(true);
              paymentBtn.current.click();
            }}
          >
            {loading ? (
              <Translate id="PAYMENT.PROCESSING" />
            ) : (
              <Translate id="PAYMENT.FINISH" />
            )}
          </ButtonLoadingCtrl>
        </OrderCtrl>
      </BoxWrapper>
    </div>
  ) : null;
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(PreviewPage)`
  margin-top: 60px;

  .back-icon {
    margin-bottom: 20px;
    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      position: absolute;
      top: 0;
      left: 24px;
      margin-bottom: 0;
    }
    ${({ theme }) => `@media screen and ${theme.device.laptopXL}`} {
      left: 0;
    }
  }

  .full-width {
    width: 100%;
  }
  .order {
    ${({ theme }) => `@media screen and ${theme.device.laptopS}`} {
      min-width: 265px;
      position: absolute;
      top: 60px;
      right: 24px;
      z-index: 100;
    }
    ${({ theme }) => `@media screen and ${theme.device.laptopXL}`} {
      right: 0;
    }
  }

  .payment {
    flex-direction: column;
    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      flex-direction: row;
    }
    .payment-btn {
      width: 100%;
      &:first-child {
        margin: 10px 0 5px;
        ${({ theme }) => `@media screen and ${theme.device.laptop}`} {
          margin: 0;
        }
      }
      ${({ theme }) => `@media screen and ${theme.device.laptop}`} {
        width: calc(100% / 2 - 5px);
      }
    }
  }
`);

const PaymentBtnWrapper = styled(Flex)`
  flex-direction: column;
  align-items: center;

  & > div {
    flex-grow: 1;
    &:last-child {
      margin-top: 10px;
    }
  }
`;

const BoxWrapper = styled.div`
  display: grid;
  grid-gap: 3px;
`;
